import React, { useState, useEffect } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Typography, 
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { useTutorial } from '../contexts/TutorialContext';
import TutorialContentItem from './TutorialContentItem';

const TutorialModule = ({ module, expanded, onToggle }) => {
  const { userProgress, isModuleLocked, isModuleCompleted, dataReady } = useTutorial();
  
  // State for managing child module expansion
  const [expandedChild, setExpandedChild] = useState([]);
  const [readyToInteract, setReadyToInteract] = useState(false);
  
  const isCompleted = isModuleCompleted(module);
  
  // Check if module should be locked using the new locking logic
  const isLocked = isModuleLocked(module, false); // Always false for user mode
  
  // Check if module has required content and completion status
  const getModuleCompletionInfo = () => {
    if (!dataReady || !userProgress || !module) {
      return { hasRequired: false, showGrayCheck: false, showGreenCheck: false };
    }
    
    // Get all content from the module
    let allContent = [];
    
    if (typeof module.getAllContent === 'function') {
      allContent = module.getAllContent();
    } else {
      const collectContent = (items) => {
        const content = [];
        if (items && Array.isArray(items)) {
          items.forEach(item => {
            if (item.type === 'content') {
              content.push(item);
            } else if (item.children && Array.isArray(item.children)) {
              content.push(...collectContent(item.children));
            }
          });
        }
        return content;
      };
      allContent = collectContent(module.children || []);
    }
    
    // Get required content
    const requiredContent = allContent.filter(content => {
      const status = userProgress.getContentStatus(content.itemId);
      return status.required;
    });
    
    const hasRequired = requiredContent.length > 0;
    
    if (hasRequired) {
      // If has required content, show green check only when completed, gray check when not completed
      return {
        hasRequired: true,
        showGrayCheck: !isCompleted,
        showGreenCheck: isCompleted
      };
    } else {
      // If no required content, use original logic (only show green when all optional completed)
      return {
        hasRequired: false,
        showGrayCheck: false,
        showGreenCheck: isCompleted
      };
    }
  };
  
  const completionInfo = getModuleCompletionInfo();
  
  // Set ready to interact after data is ready and a small delay
  useEffect(() => {
    if (dataReady) {
      const timer = setTimeout(() => {
        setReadyToInteract(true);
      }, 100); // Small delay to ensure all calculations are complete
      
      return () => clearTimeout(timer);
    } else {
      setReadyToInteract(false);
    }
  }, [dataReady]);
  
  // Auto-collapse locked child modules
  useEffect(() => {
    if (dataReady && module.children && module.children.length > 0) {
      setExpandedChild(prev => prev.filter(childId => {
        const childModule = module.children.find(child => child.itemId === childId && child.type === 'module');
        return childModule && !isModuleLocked(childModule, false);
      }));
    }
  }, [dataReady, module.children, isModuleLocked]);
  
  // Handle child module expansion
  const handleChildChange = (childId) => (event, isExpanded) => {
    // Only allow changes when ready to interact
    if (!readyToInteract) return;
    
    // Find the child module to check if it's locked
    const childModule = module.children?.find(child => child.itemId === childId && child.type === 'module');
    
    if (isExpanded) {
      // Don't allow expanding locked child modules
      if (childModule && isModuleLocked(childModule, false)) {
        return;
      }
      // Add child to expanded array if not already present
      setExpandedChild(prev => prev.includes(childId) ? prev : [...prev, childId]);
    } else {
      // Remove child from expanded array
      setExpandedChild(prev => prev.filter(item => item !== childId));
    }
  };
  
  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disabled={isLocked}
      sx={{
        ...(module.parentId ? {
          // Child modules: no margins, overlapping borders
          margin: '0 !important',
          marginTop: '-1px !important',
          marginBottom: '0 !important',
          '&:first-of-type': {
            marginTop: '0 !important',
          },
        } : {
          // Top-level modules: card style with fully rounded corners, no shadow
          borderRadius: '16px !important',
          overflow: 'visible',
          backgroundColor: '#f7f7f7',
        border: 'none',
          boxShadow: 'none', // Remove shadow effect
        }),
        '&:before': {
          display: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: '#f5f5f5',
          opacity: 0.6,
        },
      }}
    >
      <AccordionSummary
        expandIcon={isLocked ? <LockIcon /> : <ExpandMoreIcon />}
        sx={{
          borderTopLeftRadius: !module.parentId ? '16px' : 0,
          borderTopRightRadius: !module.parentId ? '16px' : 0,
          backgroundColor: !module.parentId ? '#f7f7f7' : '#f6f6f6', // Top-level uses card color, child uses lighter gray
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          height: '100%', // Fill the accordion summary height
          py: 0, // Remove vertical padding to prevent height changes
        }}>
          <Box sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // Center content vertically
            minHeight: '40px', // Minimum height for content
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'Arial, sans-serif',
                  fontSize: module.parentId ? '1rem' : '1.2rem', // Larger font for top-level modules
                fontWeight: 700,
                color: isLocked ? '#999' : '#333',
                  lineHeight: 1.2, // Fixed line height
                  mb: module.description ? 0.25 : 0, // Consistent spacing
              }}
            >
              {module.title}
            </Typography>
              {/* Completion indicator next to title */}
              {completionInfo.showGreenCheck && (
                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: '1.2rem' }} />
              )}
              {completionInfo.showGrayCheck && (
                <CheckCircleIcon sx={{ color: '#999', fontSize: '1.2rem' }} />
              )}
            </Box>
            {module.description && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isLocked ? '#999' : '#666',
                  lineHeight: 1.3, // Fixed line height
                  fontSize: '0.875rem', // Fixed font size
                }}
              >
                {module.description}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mr: 2,
            height: '40px', // Fixed height for icon area
            minWidth: '40px', // Minimum width to prevent layout shift
          }}>
            {/* Remove completion indicator from here since it's now next to title */}
          </Box>
        </Box>
      </AccordionSummary>
      
      <AccordionDetails
        sx={{
          borderBottomLeftRadius: !module.parentId ? '16px' : 0,
          borderBottomRightRadius: !module.parentId ? '16px' : 0,
          backgroundColor: !module.parentId ? '#f7f7f7' : '#f6f6f6', // Top-level uses card color, child uses lighter gray
        }}
      >
        {/* Render child modules recursively */}
        <Box sx={{ 
          py: module.parentId ? 0 : 3, // No padding for child modules, normal padding for top-level
          px: module.parentId ? 0 : 0, // No horizontal padding for child modules
        }}>
        {module.children && module.children.map((child) => {
          if (child.type === 'module') {
            return (
              <TutorialModule
                key={child.itemId}
                module={child}
                  expanded={expandedChild.includes(child.itemId)}
                  onToggle={handleChildChange(child.itemId)}
              />
            );
          } else if (child.type === 'content') {
            return (
              <TutorialContentItem
                key={child.itemId}
                content={child}
              />
            );
          }
          return null;
        })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default TutorialModule; 